import { useMemo } from "react";
import type { MicroAppResponse } from "@/api";
import {
  addMicroAppTeamMember,
  addMicroAppUserMember,
  getMicroAppMembersOptions,
  getMicroAppMembersQueryKey,
  removeMicroAppTeamMember,
  removeMicroAppUserMember,
  updateMicroAppTeamMember,
  updateMicroAppUserMember,
} from "@/api";
import { type MembershipApi, MembershipPanel } from "@/components/build/MembershipPanel";
import { useCurrentUser } from "@/features/account/useCurrentUser";

/** Micro-apps' Access tab: the membership model, via the shared MembershipPanel. */
export function MicroAppAccessTab({ app }: Readonly<{ app: MicroAppResponse }>) {
  const me = useCurrentUser();

  const api = useMemo<MembershipApi>(
    () => ({
      membersOptions: () => getMicroAppMembersOptions({ path: { app_uuid: app.uuid } }),
      membersQueryKey: () => getMicroAppMembersQueryKey({ path: { app_uuid: app.uuid } }),
      addUser: (id, role) =>
        addMicroAppUserMember({ path: { app_uuid: app.uuid }, body: { userUuid: id, role }, throwOnError: true }),
      updateUserRole: (id, role) =>
        updateMicroAppUserMember({ path: { app_uuid: app.uuid, user_uuid: id }, body: { role }, throwOnError: true }),
      removeUser: (id) => removeMicroAppUserMember({ path: { app_uuid: app.uuid, user_uuid: id }, throwOnError: true }),
      updateTeamRole: (id, role) =>
        updateMicroAppTeamMember({ path: { app_uuid: app.uuid, team_uuid: id }, body: { role }, throwOnError: true }),
      removeTeam: (id) => removeMicroAppTeamMember({ path: { app_uuid: app.uuid, team_uuid: id }, throwOnError: true }),
      addTeam: (id, role) =>
        addMicroAppTeamMember({ path: { app_uuid: app.uuid }, body: { teamUuid: id, role }, throwOnError: true }),
    }),
    [app.uuid],
  );

  return <MembershipPanel api={api} currentUserUuid={me.data?.user.uuid} />;
}
